import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { apiGet, apiPost, apiDelete, apiPut } from "../../api";
import { SvgIcon } from "../SvgIcon";
import { t, Locale } from "../../i18n";

// ─── Icons ────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/>
  </svg>
);

// ─── Types ────────────────────────────────────────────────────────────────

interface Subscription {
  id: number;
  plan_name: string;
  price_cents: number;
  billing_interval: "monthly" | "annual";
  current_period_end: string | null;
  grace_start: string | null;
  storage_gb: number;
  domain_count: number;
  alias_count: number;
}

interface NextPayment {
  amount_cents: number;
  date: string;
  payment_method: { brand: string; last4: string } | null;
}

interface FeatureLocks {
  storage_slider: { locked: boolean; max_gb: number };
  domain_count: { locked: boolean; value: number | null };
  alias_count: { locked: boolean; value: number | null };
  plan_change: { locked: boolean };
  payment_methods: { locked: boolean };
}

interface PlanLimits {
  hourly_send_limit: number | null;
  daily_send_limit: number | null;
  max_attachment_mb: number | null;
  max_aliases: number | null;
  custom_domains_allowed: boolean;
  plan_tier: string;
  description: string | null;
}

interface Reseller {
  id: number;
  brand_name: string;
  white_label: boolean;
}

interface StatusResponse {
  status: "active" | "grace_period" | "suspended" | "canceled" | "canceling" | "trial" | "none";
  subscription: Subscription | null;
  next_payment: NextPayment | null;
  feature_locks: FeatureLocks;
  plan_limits: PlanLimits | null;
  reseller: Reseller | null;
}

interface PaymentMethod {
  id: number;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  priority: number;
}

interface Invoice {
  id: string;
  date: number;
  description: string;
  amount: number;
  status: string;
}

interface DnsStatus {
  mx_ok: boolean;
  spf_ok: boolean;
  dkim_ok: boolean;
  all_ok: boolean;
}

interface Propagation {
  status: "pending" | "propagated" | "failed";
  checks_run: number;
  next_check_at: string;
}

interface Domain {
  id: number;
  domain: string;
  dkim_selector: string;
  dns_status: DnsStatus;
  propagation: Propagation | null;
  last_checked_at: string | null;
  activated_at: string | null;
  created_at: string;
}

interface QuotaInfo {
  quota_bytes: number;
  used_bytes: number;
  percent: number;
}

interface PricingConfig {
  storage_price_per_gb: number;
  storage_included_personal_gb: number;
  storage_included_business_gb: number;
  storage_max_self_serve_gb: number;
  domain_price_per_month: number;
  alias_price_per_month: number;
  business_inbox_price_per_month: number;
  personal_base_price: number;
  business_base_price: number;
  inbox_price_tier_1: number;
  inbox_price_tier_2: number;
  inbox_price_tier_3: number;
  inbox_price_tier_4: number;
  inbox_price_tier_5: number;
  inbox_price_tier_6: number;
  inbox_included_storage_gb: number;
}

// ─── Helper Functions ──────────────────────────────────────────────────────

function calculateInboxCost(count: number, pricing: PricingConfig): number {
  if (count <= 0) return 0;

  let total = 0;
  let remaining = count;

  // Inboxes 1-10 at tier_1 price
  const tier1Count = Math.min(remaining, 10);
  total += tier1Count * pricing.inbox_price_tier_1;
  remaining -= tier1Count;

  // Inboxes 11-20 at tier_2 price
  if (remaining > 0) {
    const tier2Count = Math.min(remaining, 10);
    total += tier2Count * pricing.inbox_price_tier_2;
    remaining -= tier2Count;
  }

  // Inboxes 21-30 at tier_3 price
  if (remaining > 0) {
    const tier3Count = Math.min(remaining, 10);
    total += tier3Count * pricing.inbox_price_tier_3;
    remaining -= tier3Count;
  }

  // Inboxes 31-40 at tier_4 price
  if (remaining > 0) {
    const tier4Count = Math.min(remaining, 10);
    total += tier4Count * pricing.inbox_price_tier_4;
    remaining -= tier4Count;
  }

  // Inboxes 41-50 at tier_5 price
  if (remaining > 0) {
    const tier5Count = Math.min(remaining, 10);
    total += tier5Count * pricing.inbox_price_tier_5;
    remaining -= tier5Count;
  }

  // Inboxes 51+ at tier_6 price
  if (remaining > 0) {
    total += remaining * pricing.inbox_price_tier_6;
  }

  return total;
}

// Helper function to get the effective next tier price for the next inbox
function getNextInboxPrice(count: number, pricing: PricingConfig): number {
  if (count < 10) return pricing.inbox_price_tier_1;
  if (count < 20) return pricing.inbox_price_tier_2;
  if (count < 30) return pricing.inbox_price_tier_3;
  if (count < 40) return pricing.inbox_price_tier_4;
  if (count < 50) return pricing.inbox_price_tier_5;
  return pricing.inbox_price_tier_6;
}

// Helper function to get current tier and discount percentage
function getCurrentTierInfo(count: number): { tier: number; discount: number } {
  if (count === 0) return { tier: 0, discount: 0 };
  if (count <= 10) return { tier: 1, discount: 0 };
  if (count <= 20) return { tier: 2, discount: 10 };
  if (count <= 30) return { tier: 3, discount: 20 };
  if (count <= 40) return { tier: 4, discount: 30 };
  if (count <= 50) return { tier: 5, discount: 40 };
  return { tier: 6, discount: 50 };
}

// Helper function to get inboxes needed for next tier
function getInboxesNeededForNextTier(count: number): number | null {
  if (count === 0) return 1;
  if (count < 10) return 10 - count;
  if (count < 20) return 20 - count;
  if (count < 30) return 30 - count;
  if (count < 40) return 40 - count;
  if (count < 50) return 50 - count;
  return null; // Already at tier 6
}

// ─── Stripe Initialization ────────────────────────────────────────────────────

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "");

// ─── Payment Element Form Component ───────────────────────────────────────────

function PaymentElementForm({ onClose, onSuccess, locale }: { onClose: () => void; onSuccess: () => void; locale: Locale }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { setupIntent, error: stripeError } = await stripe.confirmSetup({
        elements,
        redirect: "if_required"
      });

      if (stripeError) {
        setError(stripeError.message || t(locale, "payments.card.failed"));
        setIsProcessing(false);
        return;
      }

      if (!setupIntent.payment_method) {
        setError(t(locale, "payments.card.failed"));
        setIsProcessing(false);
        return;
      }

      const paymentMethodId = typeof setupIntent.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent.payment_method.id;

      const res = await apiPost<{ method: PaymentMethod }>("/v1/payments/methods", {
        paymentMethodId
      });

      if (res.ok) {
        onSuccess();
        onClose();
      } else {
        setError(res.error || t(locale, "payments.card.failed"));
        setIsProcessing(false);
      }
    } catch (err: any) {
      setError(err?.message || t(locale, "payments.card.failed"));
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <div style={{ color: "#ef4444", fontSize: "12px", marginTop: "12px", marginBottom: "12px" }}>
          {error}
        </div>
      )}
      <div style={{ display: "flex", gap: "12px", marginTop: "20px" }}>
        <button
          type="submit"
          disabled={!stripe || !elements || isProcessing}
          style={{
            flex: 1,
            padding: "10px 20px",
            backgroundColor: isProcessing ? "var(--color-text-secondary)" : "var(--color-primary)",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: isProcessing ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: 600,
            opacity: isProcessing ? 0.6 : 1
          }}
        >
          {isProcessing ? t(locale, "payments.card.saving") : t(locale, "payments.card.save")}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          style={{
            padding: "10px 20px",
            backgroundColor: "transparent",
            color: "var(--color-text)",
            border: "1px solid var(--color-border)",
            borderRadius: "6px",
            cursor: isProcessing ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: 600
          }}
        >
          {t(locale, "common.cancel")}
        </button>
      </div>
    </form>
  );
}

// ─── PaymentsSettings Component ────────────────────────────────────────────

export function PaymentsSettings(props: { locale: Locale }) {
  const locale = props.locale;
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [pricing, setPricing] = useState<PricingConfig>({
    storage_price_per_gb: 0.15,
    storage_included_personal_gb: 5,
    storage_included_business_gb: 15,
    storage_max_self_serve_gb: 200,
    domain_price_per_month: 2.00,
    alias_price_per_month: 0.50,
    business_inbox_price_per_month: 3.00,
    personal_base_price: 5.00,
    business_base_price: 7.00,
    inbox_price_tier_1: 3.00,
    inbox_price_tier_2: 2.70,
    inbox_price_tier_3: 2.40,
    inbox_price_tier_4: 2.10,
    inbox_price_tier_5: 1.80,
    inbox_price_tier_6: 1.50,
    inbox_included_storage_gb: 5,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Plan Configuration Modal state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [storageGb, setStorageGb] = useState(5);
  const [domainCount, setDomainCount] = useState(1);
  const [aliasCount, setAliasCount] = useState(5);
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("monthly");
  const [updating, setUpdating] = useState(false);

  // Business Plan Configuration Modal state
  const [showBusinessPlanModal, setShowBusinessPlanModal] = useState(false);
  const [businessStorageGb, setBusinessStorageGb] = useState(15);
  const [businessDomainCount, setBusinessDomainCount] = useState(1);
  const [businessAliasCount, setBusinessAliasCount] = useState(5);
  const [businessBillingInterval, setBusinessBillingInterval] = useState<"monthly" | "annual">("monthly");
  const [businessUpdating, setBusinessUpdating] = useState(false);

  // Dialogs
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showDeleteMethod, setShowDeleteMethod] = useState<number | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [storageRequest, setStorageRequest] = useState(250);

  // Setup Intent state for Payment Element
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
  const [setupIntentLoading, setSetupIntentLoading] = useState(false);
  const [setupIntentError, setSetupIntentError] = useState<string | null>(null);

  // Fetch on mount
  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    const [statusRes, methodsRes, invoicesRes, domainsRes, quotaRes, pricingRes] = await Promise.all([
      apiGet<StatusResponse>("/v1/payments/status"),
      apiGet<{ methods: PaymentMethod[] }>("/v1/payments/methods"),
      apiGet<{ invoices: Invoice[] }>("/v1/payments/invoices"),
      apiGet<{ domains: Domain[] }>("/v1/domains"),
      apiGet<QuotaInfo>("/v1/mailboxes/quota"),
      apiGet<PricingConfig>("/v1/config/pricing")
    ]);

    if (statusRes.ok) {
      setStatus(statusRes.data);
      if (statusRes.data.subscription) {
        setStorageGb(statusRes.data.subscription.storage_gb);
        setDomainCount(statusRes.data.subscription.domain_count);
        setBillingInterval(statusRes.data.subscription.billing_interval);
      }
    } else {
      setError(`Failed to load subscription status: ${statusRes.error}`);
    }

    if (!methodsRes.ok) {
      setError(prev => prev ? `${prev}; ${methodsRes.error}` : `Failed to load payment methods: ${methodsRes.error}`);
    } else {
      setPaymentMethods(methodsRes.data.methods);
    }

    if (!invoicesRes.ok) {
      setError(prev => prev ? `${prev}; ${invoicesRes.error}` : `Failed to load invoices: ${invoicesRes.error}`);
    } else {
      setInvoices(invoicesRes.data.invoices);
    }

    if (!domainsRes.ok) {
      setError(prev => prev ? `${prev}; ${domainsRes.error}` : `Failed to load domains: ${domainsRes.error}`);
    } else {
      setDomains(domainsRes.data.domains);
    }

    if (quotaRes.ok) {
      setQuota(quotaRes.data);
    }

    if (pricingRes.ok) {
      setPricing(pricingRes.data);
    }

    setLoading(false);
  }

  function formatCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString();
  }

  function formatBytes(bytes: number): string {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case "active": return "var(--color-success)";
      case "grace_period": return "var(--color-warning)";
      case "suspended": return "var(--color-error)";
      case "canceling": return "var(--color-info)";
      default: return "var(--color-neutral)";
    }
  }

  function getStatusBg(status: string): string {
    switch (status) {
      case "active": return "#dcfce7";
      case "grace_period": return "#fef3c7";
      case "suspended": return "#fee2e2";
      case "canceling": return "#dbeafe";
      default: return "#f3f4f6";
    }
  }

  function getPlanDisplayName(): string {
    if (status?.reseller) {
      if (status.reseller.white_label) {
        return `${status.reseller.brand_name} EMAIL`;
      }
      return t(locale, "payments.planName.professional");
    }
    if (status?.subscription) {
      return t(locale, "payments.planName.personal");
    }
    return t(locale, "payments.planName.free");
  }

  async function fetchSetupIntent() {
    setSetupIntentLoading(true);
    setSetupIntentError(null);
    const result = await apiPost<{ clientSecret: string }>("/v1/payments/setup-intent", {});
    if (result.ok) {
      setSetupIntentClientSecret(result.data.clientSecret);
    } else {
      setSetupIntentError(result.error || t(locale, "payments.card.setupIntentFailed"));
    }
    setSetupIntentLoading(false);
  }

  async function handleUpdatePlan() {
    setUpdating(true);
    const result = await apiPost("/v1/payments/subscribe", {
      billing_interval: billingInterval,
      storage_gb: storageGb,
      domain_count: domainCount,
      alias_count: aliasCount
    });
    if (result.ok) {
      await fetchAll();
    } else {
      setError(result.error);
    }
    setUpdating(false);
  }

  async function handleCancel() {
    const result = await apiPost("/v1/payments/cancel", {});
    if (result.ok) {
      setShowCancelConfirm(false);
      await fetchAll();
    } else {
      setError(result.error);
    }
  }

  async function handleRequestRefund() {
    const result = await apiPost("/v1/payments/request-refund", { reason: refundReason });
    if (result.ok) {
      setShowRefundConfirm(false);
      setRefundReason("");
      alert(t(locale, "payments.alert.refundSubmitted"));
      await fetchAll();
    } else {
      setError(result.error);
    }
  }

  async function handleRequestStorage() {
    const result = await apiPost("/v1/payments/request-storage", { requested_gb: storageRequest });
    if (result.ok) {
      setShowStorageModal(false);
      alert(t(locale, "payments.alert.storageSubmitted"));
    } else {
      setError(result.error);
    }
  }

  async function handleDeleteMethod() {
    if (!showDeleteMethod) return;
    const result = await apiDelete(`/v1/payments/methods/${showDeleteMethod}`);
    if (result.ok) {
      setShowDeleteMethod(null);
      await fetchAll();
    } else {
      setError(result.error);
    }
  }

  async function handleSetDefault(methodId: number) {
    const result = await apiPut(`/v1/payments/methods/${methodId}/default`, {});
    if (result.ok) {
      await fetchAll();
    } else {
      setError(result.error);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: "20px", maxWidth: "900px" }}>
        <h2>{t(locale, "payments.title")}</h2>
        <div style={{ padding: "20px", textAlign: "center", color: "var(--color-text-secondary)" }}>
          {t(locale, "payments.loading")}
        </div>
      </div>
    );
  }

  const isFree = !status || status.status === "none";
  const hasPaid = !!status?.subscription;
  const needsPaymentMethod = hasPaid && paymentMethods.length === 0;
  const planChanged = hasPaid && (storageGb !== status!.subscription!.storage_gb || domainCount !== status!.subscription!.domain_count);

  return (
    <div style={{ padding: "20px", maxWidth: "900px" }}>
      <h2>{t(locale, "payments.title")}</h2>

      {error && (
        <div style={{
          marginBottom: "20px",
          padding: "12px 16px",
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          color: "var(--color-error)",
          borderRadius: "6px",
          fontSize: "14px"
        }}>
          {error}
        </div>
      )}

      {/* ─── SECTION 1: Current Plan ───────────────────────────────────── */}
      {isFree ? (
        <div style={{
          marginBottom: "30px",
          padding: "20px 24px",
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderLeft: "3px solid #3b82f6",
          borderRadius: "8px"
        }}>
          {/* Title Row with Inline Badge */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
            <h2 style={{ marginTop: 0, marginBottom: "0", fontSize: "24px", fontWeight: 700 }}>
              {getPlanDisplayName()}
            </h2>
            <div style={{
              padding: "4px 10px",
              backgroundColor: "var(--color-bg-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 500,
              color: "var(--color-text-secondary)",
              whiteSpace: "nowrap"
            }}>
              {t(locale, "payments.currentPlan")}
            </div>
          </div>

          {/* Feature List */}
          {status?.plan_limits && (
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", flexDirection: "column", fontSize: "14px", lineHeight: "1.5" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                  <div style={{ color: "#4ade80", display: "flex" }}><CheckIcon /></div>
                  <span>{status.plan_limits.description?.match(/\d+GB/)?.[0] ?? '3 GB'} storage{quota && quota.used_bytes > 0 && formatBytes(quota.used_bytes) !== "0.00 GB" ? ` (using ${formatBytes(quota.used_bytes)})` : ""}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                  <div style={{ color: "#4ade80", display: "flex" }}><CheckIcon /></div>
                  <span>1 @kveja.com address</span>
                </div>
                {status.plan_limits.hourly_send_limit && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                    <div style={{ color: "#4ade80", display: "flex" }}><CheckIcon /></div>
                    <span>{status.plan_limits.hourly_send_limit} emails/hour · {status.plan_limits.daily_send_limit} emails/day</span>
                  </div>
                )}
                {status.plan_limits.max_attachment_mb && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                    <div style={{ color: "#4ade80", display: "flex" }}><CheckIcon /></div>
                    <span>{status.plan_limits.max_attachment_mb} MB max attachment size</span>
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", color: "#6b7280" }}>
                  <div style={{ display: "flex" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                    </svg>
                  </div>
                  <span>{t(locale, "payments.plan.customDomains")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#6b7280" }}>
                  <div style={{ display: "flex" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                    </svg>
                  </div>
                  <span>{t(locale, "payments.plan.additionalAliases")}</span>
                </div>
              </div>
            </div>
          )}

          {/* Divider and Section Label */}
          <div style={{ marginBottom: "24px" }}>
            <hr style={{ margin: "0 0 12px 0", border: "none", borderTop: "1px solid var(--color-border)" }} />
            <p style={{ margin: 0, fontSize: "12px", color: "var(--color-text-secondary)", fontStyle: "italic" }}>
              {t(locale, "payments.upgradePlan")}
            </p>
          </div>

          {/* Plan Cards Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "12px",
            marginTop: "16px",
            marginBottom: "24px"
          }}>
            {/* Personal Plan Card */}
            <div style={{
              padding: "14px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--line)",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              transition: "border-color 200ms ease"
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#3b82f6";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "var(--line)";
            }}>
              <h3 style={{ marginTop: 0, marginBottom: "6px", fontSize: "16px" }}>{t(locale, "payments.plan.personal")}</h3>
              <p style={{ fontSize: "16px", fontWeight: 600, margin: "0 0 12px 0" }}>{t(locale, "payments.plan.fromPrice.personal")}</p>
              <div style={{ marginBottom: "12px", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.moreStorage")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.customDomains")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.additionalAliases")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.customDomainSupport")}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setStorageGb(pricing.storage_included_personal_gb);
                  setDomainCount(1);
                  setAliasCount(5);
                  setBillingInterval("monthly");
                  setShowPlanModal(true);
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  transition: "background-color 200ms ease",
                  marginTop: "auto"
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#2563eb";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#3b82f6";
                }}
              >
                {t(locale, "payments.plan.configure")}
              </button>
            </div>

            {/* Business Plan Card */}
            <div style={{
              padding: "14px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--line)",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              transition: "border-color 200ms ease"
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#3b82f6";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "var(--line)";
            }}>
              <h3 style={{ marginTop: 0, marginBottom: "6px", fontSize: "16px" }}>{t(locale, "payments.plan.business")}</h3>
              <p style={{ fontSize: "16px", fontWeight: 600, margin: "0 0 12px 0" }}>{t(locale, "payments.plan.fromPrice.business")}</p>
              <div style={{ marginBottom: "12px", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.everythingInPersonal")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.employeeInboxes")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.higherStorageLimits")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.newsletterService")}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setBusinessStorageGb(pricing.storage_included_business_gb);
                  setBusinessDomainCount(1);
                  setBusinessAliasCount(5);
                  setBusinessBillingInterval("monthly");
                  setShowBusinessPlanModal(true);
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  transition: "background-color 200ms ease",
                  marginTop: "auto"
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#2563eb";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#3b82f6";
                }}
              >
                {t(locale, "payments.plan.configure")}
              </button>
            </div>

            {/* Enterprise Plan Card */}
            <div style={{
              padding: "14px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--line)",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              transition: "border-color 200ms ease"
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#3b82f6";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "var(--line)";
            }}>
              <h3 style={{ marginTop: 0, marginBottom: "6px", fontSize: "16px" }}>{t(locale, "payments.plan.enterprise")}</h3>
              <p style={{ fontSize: "16px", fontWeight: 600, margin: "0 0 12px 0" }}>{t(locale, "payments.plan.customPricing")}</p>
              <div style={{ marginBottom: "12px", flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.unlimitedStorage")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.unlimitedDomains")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.dedicatedIp")}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" }}>
                  <div style={{ color: "#4ade80", display: "flex", width: "16px", height: "16px" }}><CheckIcon /></div>
                  <span>{t(locale, "payments.plan.slaSupport")}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  alert(t(locale, "payments.plan.enterprise.contactAlert"));
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  transition: "background-color 200ms ease",
                  marginTop: "auto"
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#4b5563";
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = "#6b7280";
                }}
              >
                {t(locale, "payments.plan.contactUs")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          marginBottom: "30px",
          padding: "20px",
          backgroundColor: "var(--color-bg-secondary)",
          borderRadius: "8px",
          border: "1px solid var(--color-border)"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" }}>
            <div>
              <h3 style={{ marginTop: 0, marginBottom: "5px" }}>{getPlanDisplayName()}</h3>
              <div style={{ fontSize: "13px", color: "var(--color-text-secondary)" }}>
                Storage: {status!.subscription!.storage_gb} GB{quota && quota.quota_bytes > 0 ? ` (using ${formatBytes(quota.used_bytes)})` : ""} • Domains: {status!.subscription!.domain_count}
              </div>
            </div>
            {hasPaid && (
              <div
                style={{
                  padding: "6px 12px",
                  backgroundColor: getStatusBg(status!.status),
                  color: getStatusColor(status!.status),
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap"
                }}
              >
                {status!.status.toUpperCase().replace(/_/g, " ")}
              </div>
            )}
          </div>

          {/* Plan Limits */}
          {status?.plan_limits && (
            <div style={{ marginBottom: "15px", fontSize: "13px", color: "var(--color-text-secondary)", lineHeight: "1.6" }}>
              {status.plan_limits.max_aliases !== undefined && (
                <div>Aliases: {status.plan_limits.max_aliases === 0 ? "1 @kveja.com only" : `${status.plan_limits.max_aliases} @kveja.com${status.plan_limits.max_aliases === 1 ? "" : "es"}`}</div>
              )}
              {status.plan_limits.hourly_send_limit && (
                <div>{t(locale, "payments.planLimits.sending", { hourly: String(status.plan_limits.hourly_send_limit), daily: String(status.plan_limits.daily_send_limit) })}</div>
              )}
              {status.plan_limits.max_attachment_mb && (
                <div>{t(locale, "payments.planLimits.maxAttachment", { mb: String(status.plan_limits.max_attachment_mb) })}</div>
              )}
            </div>
          )}

          {/* Next Payment Info */}
          {hasPaid && status!.next_payment && (
            <div style={{ marginBottom: "15px", fontSize: "13px", backgroundColor: "rgba(59, 130, 246, 0.05)", padding: "10px", borderRadius: "4px" }}>
              <div>Next billing: {formatDate(status!.next_payment.date)}</div>
              <div>Amount: {formatCents(status!.next_payment.amount_cents)}</div>
              {status!.next_payment.payment_method && (
                <div>Billed to: {status!.next_payment.payment_method.brand.toUpperCase()} •••• {status!.next_payment.payment_method.last4}</div>
              )}
            </div>
          )}

          {/* Status Alerts */}
          {hasPaid && status!.status === "grace_period" && status!.subscription!.grace_start && (
            <div style={{ padding: "10px", backgroundColor: "rgba(234, 179, 8, 0.1)", borderRadius: "4px", marginBottom: "10px", fontSize: "13px", color: "var(--color-warning)" }}>
              Payment failed. Grace period until {formatDate(status!.subscription!.grace_start)}.
            </div>
          )}

          {hasPaid && status!.status === "suspended" && (
            <div style={{ padding: "10px", backgroundColor: "rgba(239, 68, 68, 0.1)", borderRadius: "4px", marginBottom: "10px", fontSize: "13px", color: "var(--color-error)" }}>
              Account suspended. Please update payment method.
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={() => {
              setStorageGb(status!.subscription!.storage_gb);
              setDomainCount(status!.subscription!.domain_count);
              setAliasCount(5);
              setBillingInterval(status!.subscription!.billing_interval);
              setShowPlanModal(true);
            }}
            style={{
              padding: "12px 24px",
              backgroundColor: "var(--color-primary)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600
            }}
          >
            {t(locale, "payments.modifyPlan")}
          </button>
        </div>
      )}


      {/* ─── SECTION 2: Payment Methods ───────────────────────────────── */}
      {!status?.feature_locks.payment_methods.locked && (
        <div style={{ marginBottom: "30px" }}>
          <h3>{t(locale, "payments.section.paymentMethods")}</h3>

          {paymentMethods.length === 0 ? (
            <div style={{
              padding: "15px",
              backgroundColor: "var(--color-bg-secondary)",
              borderRadius: "6px",
              marginBottom: "15px",
              fontSize: "14px"
            }}>
              {t(locale, "payments.noPaymentMethods")}
            </div>
          ) : (
            <div style={{ marginBottom: "15px" }}>
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px",
                    marginBottom: "8px",
                    backgroundColor: "var(--color-bg-secondary)",
                    borderRadius: "6px",
                    border: "1px solid var(--color-border)"
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>
                      {method.brand.toUpperCase()} •••• {method.last4}
                      {method.is_default && (
                        <span style={{
                          marginLeft: "8px",
                          padding: "2px 6px",
                          backgroundColor: "var(--color-success)",
                          color: "white",
                          borderRadius: "3px",
                          fontSize: "11px"
                        }}>
                          DEFAULT
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
                      Expires {method.exp_month}/{method.exp_year}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {!method.is_default && (
                      <button
                        onClick={() => handleSetDefault(method.id)}
                        style={{
                          padding: "6px 12px",
                          fontSize: "12px",
                          border: "1px solid var(--color-border)",
                          borderRadius: "4px",
                          cursor: "pointer",
                          backgroundColor: "var(--color-bg-secondary)"
                        }}
                      >
                        {t(locale, "payments.paymentMethod.setDefault")}
                      </button>
                    )}
                    <button
                      onClick={() => setShowDeleteMethod(method.id)}
                      style={{
                        padding: "6px 12px",
                        fontSize: "12px",
                        border: "1px solid var(--color-error)",
                        borderRadius: "4px",
                        cursor: "pointer",
                        color: "var(--color-error)",
                        backgroundColor: "transparent"
                      }}
                    >
                      {t(locale, "common.delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => {
              setShowAddCard(true);
              fetchSetupIntent();
            }}
            style={{
              padding: "10px 16px",
              backgroundColor: "var(--color-primary)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600
            }}
          >
            {t(locale, "payments.addCard")}
          </button>
        </div>
      )}

      {/* ─── SECTION 3: Billing History ───────────────────────────────── */}
      <div style={{ marginBottom: "30px" }}>
        <h3>{t(locale, "payments.section.billingHistory")}</h3>

        {invoices.length === 0 ? (
          <div style={{
            padding: "15px",
            backgroundColor: "var(--color-bg-secondary)",
            borderRadius: "6px",
            fontSize: "14px"
          }}>
            {t(locale, "payments.noBillingHistory")}
          </div>
        ) : (
          <div>
            {invoices.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px",
                  borderBottom: "1px solid var(--color-border)",
                  fontSize: "13px"
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{inv.description}</div>
                  <div style={{ color: "var(--color-text-secondary)" }}>
                    {new Date(inv.date * 1000).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 600 }}>{formatCents(inv.amount)}</div>
                  <div style={{ color: "var(--color-text-secondary)" }}>{inv.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── SECTION 4: Danger Zone ───────────────────────────────────── */}
      {hasPaid && ["active", "trial", "canceling", "grace_period"].includes(status!.status) && (
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ color: "var(--color-error)" }}>{t(locale, "payments.section.dangerZone")}</h3>
          <div style={{
            padding: "15px",
            backgroundColor: "rgba(239, 68, 68, 0.05)",
            border: "2px solid rgba(239, 68, 68, 0.2)",
            borderRadius: "6px"
          }}>
            <button
              onClick={() => setShowCancelConfirm(true)}
              style={{
                padding: "10px 16px",
                backgroundColor: "var(--color-error)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                marginRight: "10px"
              }}
            >
              {t(locale, "payments.cancelSubscription")}
            </button>
            {status!.subscription && new Date(status!.subscription.current_period_end || "") > new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) && (
              <button
                onClick={() => setShowRefundConfirm(true)}
                style={{
                  padding: "10px 16px",
                  backgroundColor: "var(--color-warning)",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600
                }}
              >
                {t(locale, "payments.requestRefund")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── DIALOGS ───────────────────────────────────────────────────── */}

      {/* Plan Configuration Modal Overlay */}
      {showPlanModal && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(4px)",
              zIndex: 999,
              animation: "fadeIn 150ms ease-out"
            }}
            onClick={() => setShowPlanModal(false)}
          />

          {/* Modal Content */}
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: "520px",
              maxHeight: "90vh",
              backgroundColor: "var(--surface)",
              borderRadius: "12px",
              border: "1px solid var(--line)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
              zIndex: 1000,
              animation: "fadeIn 150ms ease-out",
              overflowY: "auto",
              overflowX: "hidden"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <div style={{ position: "relative", paddingBottom: "8px" }}>
              <button
                onClick={() => setShowPlanModal(false)}
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  backgroundColor: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "var(--color-text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "24px", paddingTop: "16px" }}>
              <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px" }}>{t(locale, "payments.planConfig.title")}</h3>

              {/* Storage Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  Storage: {storageGb} GB
                </label>
                <input
                  type="range"
                  min="5"
                  max="200"
                  step="1"
                  value={storageGb}
                  onChange={(e) => setStorageGb(Number(e.target.value))}
                  style={{ width: "100%", marginBottom: "6px" }}
                />
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)", marginBottom: "8px" }}>
                  {storageGb <= 5
                    ? t(locale, "payments.planConfig.storageIncluded")
                    : `+${storageGb - 5}GB × $${pricing.storage_price_per_gb.toFixed(2)} = $${(((storageGb - 5) * pricing.storage_price_per_gb) * (billingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/${billingInterval === "monthly" ? "mo" : "yr"}`}
                </div>
                {storageGb > 200 && (
                  <button
                    onClick={() => {
                      setShowPlanModal(false);
                      setShowStorageModal(true);
                    }}
                    style={{
                      padding: "0",
                      backgroundColor: "transparent",
                      border: "none",
                      color: "var(--color-primary)",
                      cursor: "pointer",
                      fontSize: "12px",
                      textDecoration: "underline"
                    }}
                  >
                    {t(locale, "payments.planConfig.needMoreStorage")}
                  </button>
                )}
              </div>

              {/* Custom Domains Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.customDomains")}
                </label>

                {domains.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--color-text-secondary)" }}>
                      {t(locale, "payments.planConfig.yourDomains")}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {domains.map((domain) => (
                        <div
                          key={domain.id}
                          style={{
                            padding: "8px",
                            backgroundColor: "var(--color-bg-primary)",
                            borderRadius: "4px",
                            fontSize: "12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{domain.domain}</div>
                            <div style={{ color: "var(--color-text-secondary)", fontSize: "11px" }}>
                              Status: {domain.activated_at ? t(locale, "payments.planConfig.statusActive") : t(locale, "payments.planConfig.statusPending")}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setShowPlanModal(false);
                              // Navigate to domain settings
                              // This would be handled by routing or context
                            }}
                            style={{
                              padding: "4px 8px",
                              fontSize: "11px",
                              border: "1px solid var(--color-border)",
                              borderRadius: "3px",
                              cursor: "pointer",
                              backgroundColor: "transparent"
                            }}
                          >
                            {t(locale, "common.manage")}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                  <button
                    onClick={() => setDomainCount(Math.max(1, domainCount - 1))}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: "14px", fontWeight: 600, minWidth: "30px", textAlign: "center" }}>
                    {domainCount}
                  </span>
                  <button
                    onClick={() => setDomainCount(domainCount + 1)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
                  {domainCount === 0
                    ? t(locale, "payments.planConfig.domainOnly")
                    : domainCount === 1
                    ? t(locale, "payments.planConfig.domainIncluded")
                    : `$${(((domainCount - 1) * pricing.domain_price_per_month) * (billingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/${billingInterval === "monthly" ? "mo" : "yr"} for ${domainCount - 1} additional ${domainCount === 2 ? "domain" : "domains"}`}
                </div>
              </div>

              {/* More Aliases Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.numberOfAliases")}
                </label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                  <button
                    onClick={() => setAliasCount(Math.max(5, aliasCount - 1))}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: "14px", fontWeight: 600, minWidth: "30px", textAlign: "center" }}>
                    {aliasCount}
                  </span>
                  <button
                    onClick={() => setAliasCount(aliasCount + 1)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
                  {aliasCount <= 5
                    ? t(locale, "payments.planConfig.aliasesIncluded")
                    : `$${(((aliasCount - 5) * pricing.alias_price_per_month) * (billingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/${billingInterval === "monthly" ? "mo" : "yr"} for ${aliasCount - 5} additional ${aliasCount === 6 ? "alias" : "aliases"}`}
                </div>
              </div>

              {/* Billing Interval Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.billing")}
                </label>
                <div style={{ display: "flex", gap: "10px" }}>
                  {["monthly", "annual"].map(interval => (
                    <button
                      key={interval}
                      onClick={() => setBillingInterval(interval as "monthly" | "annual")}
                      style={{
                        padding: "8px 16px",
                        border: billingInterval === interval ? "2px solid var(--color-primary)" : "1px solid var(--color-border)",
                        borderRadius: "4px",
                        backgroundColor: billingInterval === interval ? "rgba(59, 130, 246, 0.1)" : "transparent",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: billingInterval === interval ? 600 : 400
                      }}
                    >
                      {interval === "monthly" ? t(locale, "common.monthly") : t(locale, "common.yearly")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div style={{
                padding: "12px",
                backgroundColor: "rgba(59, 130, 246, 0.05)",
                borderRadius: "4px",
                marginBottom: "20px",
                fontSize: "13px"
              }}>
                {(() => {
                  const monthlyTotal = pricing.personal_base_price + ((storageGb - pricing.storage_included_personal_gb) * pricing.storage_price_per_gb) + ((domainCount - 1) * pricing.domain_price_per_month) + ((aliasCount - 5) * pricing.alias_price_per_month);
                  const yearlyMultiplier = billingInterval === "annual" ? 12 * 0.83 : 1;
                  const basePlanPrice = pricing.personal_base_price * yearlyMultiplier;
                  const storageAddonPrice = (storageGb - pricing.storage_included_personal_gb) * pricing.storage_price_per_gb * yearlyMultiplier;
                  const domainAddonPrice = (domainCount - 1) * pricing.domain_price_per_month * yearlyMultiplier;
                  const aliasAddonPrice = (aliasCount - 5) * pricing.alias_price_per_month * yearlyMultiplier;
                  const totalPrice = monthlyTotal * yearlyMultiplier;
                  const period = billingInterval === "monthly" ? "mo" : "yr";

                  return (
                    <>
                      <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                        <span>{t(locale, "payments.planConfig.summary.basePlan")}</span>
                        <span>${basePlanPrice.toFixed(2)}/{period}</span>
                      </div>
                      {storageGb > 5 && (
                        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                          <span>{t(locale, "payments.planConfig.summary.storageAddon")}</span>
                          <span>${storageAddonPrice.toFixed(2)}/{period}</span>
                        </div>
                      )}
                      {domainCount > 1 && (
                        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                          <span>{t(locale, "payments.planConfig.summary.domainAddons")}</span>
                          <span>${domainAddonPrice.toFixed(2)}/{period}</span>
                        </div>
                      )}
                      {aliasCount > 5 && (
                        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                          <span>{t(locale, "payments.planConfig.summary.aliasAddons")}</span>
                          <span>${aliasAddonPrice.toFixed(2)}/{period}</span>
                        </div>
                      )}
                      <div style={{ borderTop: "1px solid rgba(59, 130, 246, 0.2)", paddingTop: "8px", display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                        <span>{t(locale, "payments.planConfig.summary.total")}</span>
                        <span>${totalPrice.toFixed(2)}/{period}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={handleUpdatePlan}
                  disabled={updating || needsPaymentMethod}
                  style={{
                    flex: 1,
                    padding: "12px",
                    backgroundColor: updating ? "var(--color-text-secondary)" : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: updating ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    opacity: needsPaymentMethod ? 0.6 : 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px"
                  }}
                  title={needsPaymentMethod ? t(locale, "payments.planConfig.needPaymentMethod") : ""}
                  onMouseEnter={(e) => {
                    if (!updating && !needsPaymentMethod) {
                      (e.target as HTMLButtonElement).style.backgroundColor = "#2563eb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = "#3b82f6";
                  }}
                >
                  {updating ? t(locale, "payments.planConfig.updating") : (() => {
                    const monthlyTotal = pricing.personal_base_price + ((storageGb - pricing.storage_included_personal_gb) * pricing.storage_price_per_gb) + ((domainCount - 1) * pricing.domain_price_per_month) + ((aliasCount - 5) * pricing.alias_price_per_month);
                    const yearlyMultiplier = billingInterval === "annual" ? 12 * 0.83 : 1;
                    const totalPrice = monthlyTotal * yearlyMultiplier;
                    const period = billingInterval === "monthly" ? "mo" : "yr";
                    return (
                      <>
                        <div>{hasPaid ? t(locale, "payments.planConfig.action.updateSubscription") : t(locale, "payments.planConfig.action.createSubscription")}</div>
                        <div style={{ fontSize: "13px", opacity: 0.9 }}>${totalPrice.toFixed(2)} / {period}</div>
                      </>
                    );
                  })()}
                </button>
                <button
                  onClick={() => setShowPlanModal(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600
                  }}
                >
                  {t(locale, "common.cancel")}
                </button>
              </div>
            </div>
          </div>

          {/* Add animation styles */}
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}</style>
        </>
      )}

      {/* Business Plan Configuration Modal Overlay */}
      {showBusinessPlanModal && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(4px)",
              zIndex: 999,
              animation: "fadeIn 150ms ease-out"
            }}
            onClick={() => setShowBusinessPlanModal(false)}
          />

          {/* Modal Content */}
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: "520px",
              maxHeight: "90vh",
              backgroundColor: "var(--surface)",
              borderRadius: "12px",
              border: "1px solid var(--line)",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
              zIndex: 1000,
              animation: "fadeIn 150ms ease-out",
              overflowY: "auto",
              overflowX: "hidden"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <div style={{ position: "relative", paddingBottom: "8px" }}>
              <button
                onClick={() => setShowBusinessPlanModal(false)}
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  backgroundColor: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "var(--color-text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "24px", paddingTop: "16px" }}>
              <h3 style={{ marginTop: 0, marginBottom: "20px", fontSize: "18px" }}>{t(locale, "payments.planConfig.business.title")}</h3>

              {/* Storage Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  Storage: {businessStorageGb} GB
                </label>
                <input
                  type="range"
                  min="15"
                  max="500"
                  step="1"
                  value={businessStorageGb}
                  onChange={(e) => setBusinessStorageGb(Number(e.target.value))}
                  style={{ width: "100%", marginBottom: "6px" }}
                />
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)", marginBottom: "8px" }}>
                  {businessStorageGb <= 15
                    ? t(locale, "payments.planConfig.storageIncluded")
                    : `+${businessStorageGb - 15}GB × $${pricing.storage_price_per_gb.toFixed(2)} = $${(((businessStorageGb - 15) * pricing.storage_price_per_gb) * (businessBillingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/${businessBillingInterval === "monthly" ? "mo" : "yr"}`}
                </div>
                {businessStorageGb >= 500 && (
                  <button
                    onClick={() => {
                      setShowBusinessPlanModal(false);
                      setShowStorageModal(true);
                    }}
                    style={{
                      padding: "0",
                      backgroundColor: "transparent",
                      border: "none",
                      color: "var(--color-primary)",
                      cursor: "pointer",
                      fontSize: "12px",
                      textDecoration: "underline"
                    }}
                  >
                    {t(locale, "payments.planConfig.volumePricing")}
                  </button>
                )}
              </div>

              {/* Custom Domains Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.customDomains")}
                </label>

                {domains.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "var(--color-text-secondary)" }}>
                      {t(locale, "payments.planConfig.yourDomains")}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {domains.map((domain) => (
                        <div
                          key={domain.id}
                          style={{
                            padding: "8px",
                            backgroundColor: "var(--color-bg-primary)",
                            borderRadius: "4px",
                            fontSize: "12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{domain.domain}</div>
                            <div style={{ color: "var(--color-text-secondary)", fontSize: "11px" }}>
                              Status: {domain.activated_at ? t(locale, "payments.planConfig.statusActive") : t(locale, "payments.planConfig.statusPending")}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setShowBusinessPlanModal(false);
                              // Navigate to domain settings
                              // This would be handled by routing or context
                            }}
                            style={{
                              padding: "4px 8px",
                              fontSize: "11px",
                              border: "1px solid var(--color-border)",
                              borderRadius: "3px",
                              cursor: "pointer",
                              backgroundColor: "transparent"
                            }}
                          >
                            {t(locale, "common.manage")}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                  <button
                    onClick={() => setBusinessDomainCount(Math.max(1, businessDomainCount - 1))}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: "14px", fontWeight: 600, minWidth: "30px", textAlign: "center" }}>
                    {businessDomainCount}
                  </span>
                  <button
                    onClick={() => setBusinessDomainCount(businessDomainCount + 1)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
                  {businessDomainCount === 0
                    ? t(locale, "payments.planConfig.domainOnly")
                    : businessDomainCount === 1
                    ? t(locale, "payments.planConfig.domainIncluded")
                    : `$${(((businessDomainCount - 1) * pricing.domain_price_per_month) * (businessBillingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/${businessBillingInterval === "monthly" ? "mo" : "yr"} for ${businessDomainCount - 1} additional ${businessDomainCount === 2 ? "domain" : "domains"}`}
                </div>
              </div>

              {/* Number of Aliases Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.numberOfAliases")}
                </label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                  <button
                    onClick={() => setBusinessAliasCount(Math.max(5, businessAliasCount - 1))}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: "14px", fontWeight: 600, minWidth: "30px", textAlign: "center" }}>
                    {businessAliasCount}
                  </span>
                  <button
                    onClick={() => setBusinessAliasCount(businessAliasCount + 1)}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      backgroundColor: "var(--color-bg-secondary)"
                    }}
                  >
                    +
                  </button>
                </div>
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
                  {businessAliasCount <= 5
                    ? t(locale, "payments.planConfig.aliasesIncluded")
                    : `$${(((businessAliasCount - 5) * pricing.alias_price_per_month) * (businessBillingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/${businessBillingInterval === "monthly" ? "mo" : "yr"} for ${businessAliasCount - 5} additional ${businessAliasCount === 6 ? "alias" : "aliases"}`}
                </div>
              </div>

              {/* Employee Inboxes Tier Table Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "12px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.employeeInboxes")}
                </label>

                {/* Tier pricing table */}
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "12px" }}>
                  {[
                    { tierNum: 1, range: "1–10", discount: 0, price: pricing.inbox_price_tier_1 },
                    { tierNum: 2, range: "11–20", discount: 10, price: pricing.inbox_price_tier_2 },
                    { tierNum: 3, range: "21–30", discount: 20, price: pricing.inbox_price_tier_3 },
                    { tierNum: 4, range: "31–40", discount: 30, price: pricing.inbox_price_tier_4 },
                    { tierNum: 5, range: "41–50", discount: 40, price: pricing.inbox_price_tier_5 },
                    { tierNum: 6, range: "51+", discount: 50, price: pricing.inbox_price_tier_6 }
                  ].map((tier) => (
                    <div
                      key={tier.tierNum}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "12px"
                      }}
                    >
                      {/* Tier icon */}
                      <div style={{ display: "inline-flex", flexShrink: 0, color: "var(--color-text-secondary)" }}>
                        <SvgIcon name={`${tier.tierNum}-circle`} title={`Tier ${tier.tierNum}`} style={{ width: 16, height: 16 }} />
                      </div>

                      {/* Tier range */}
                      <div style={{ minWidth: "35px", fontWeight: 500 }}>
                        {tier.range}
                      </div>

                      {/* Price and discount inline */}
                      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px" }}>
                        <span>${(tier.price * (businessBillingInterval === "annual" ? 12 * 0.83 : 1)).toFixed(2)}/{businessBillingInterval === "monthly" ? "mo" : "yr"}</span>
                        {tier.discount > 0 && (
                          <span style={{ fontSize: "11px", color: "var(--color-text-secondary)" }}>
                            (-{tier.discount}%)
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Storage pool info */}
                <div style={{ fontSize: "11px", color: "var(--color-text-secondary)", marginBottom: "8px", opacity: 0.75 }}>
                  {t(locale, "payments.planConfig.inboxStoragePool", { count: String(pricing.inbox_included_storage_gb) })}
                </div>

                {/* Info note */}
                <div style={{ fontSize: "12px", color: "var(--color-text-secondary)" }}>
                  {t(locale, "payments.planConfig.addInboxHint")}
                </div>
              </div>

              {/* Billing Interval Section */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
                  {t(locale, "payments.planConfig.billing")}
                </label>
                <div style={{ display: "flex", gap: "10px" }}>
                  {["monthly", "annual"].map(interval => (
                    <button
                      key={interval}
                      onClick={() => setBusinessBillingInterval(interval as "monthly" | "annual")}
                      style={{
                        padding: "8px 16px",
                        border: businessBillingInterval === interval ? "2px solid var(--color-primary)" : "1px solid var(--color-border)",
                        borderRadius: "4px",
                        backgroundColor: businessBillingInterval === interval ? "rgba(59, 130, 246, 0.1)" : "transparent",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: businessBillingInterval === interval ? 600 : 400
                      }}
                    >
                      {interval === "monthly" ? t(locale, "common.monthly") : t(locale, "common.yearly")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Summary */}
              <div style={{
                padding: "12px",
                backgroundColor: "rgba(59, 130, 246, 0.05)",
                borderRadius: "4px",
                marginBottom: "20px",
                fontSize: "13px"
              }}>
                {(() => {
                  const monthlyTotal = pricing.business_base_price + ((businessStorageGb - pricing.storage_included_business_gb) * pricing.storage_price_per_gb) + ((businessDomainCount - 1) * pricing.domain_price_per_month) + ((businessAliasCount - 5) * pricing.alias_price_per_month);
                  const yearlyMultiplier = businessBillingInterval === "annual" ? 12 * 0.83 : 1;
                  const basePlanPrice = pricing.business_base_price * yearlyMultiplier;
                  const storageAddonPrice = (businessStorageGb - pricing.storage_included_business_gb) * pricing.storage_price_per_gb * yearlyMultiplier;
                  const domainAddonPrice = (businessDomainCount - 1) * pricing.domain_price_per_month * yearlyMultiplier;
                  const aliasAddonPrice = (businessAliasCount - 5) * pricing.alias_price_per_month * yearlyMultiplier;
                  const totalPrice = monthlyTotal * yearlyMultiplier;
                  const period = businessBillingInterval === "monthly" ? "mo" : "yr";

                  return (
                    <>
                      <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                        <span>{t(locale, "payments.planConfig.summary.basePlan")}</span>
                        <span>${basePlanPrice.toFixed(2)}/{period}</span>
                      </div>
                      {businessStorageGb > 15 && (
                        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                          <span>{t(locale, "payments.planConfig.summary.storageAddon")}</span>
                          <span>${storageAddonPrice.toFixed(2)}/{period}</span>
                        </div>
                      )}
                      {businessDomainCount > 1 && (
                        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                          <span>{t(locale, "payments.planConfig.summary.domainAddons")}</span>
                          <span>${domainAddonPrice.toFixed(2)}/{period}</span>
                        </div>
                      )}
                      {businessAliasCount > 5 && (
                        <div style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
                          <span>{t(locale, "payments.planConfig.summary.aliasAddons")}</span>
                          <span>${aliasAddonPrice.toFixed(2)}/{period}</span>
                        </div>
                      )}
                      <div style={{ borderTop: "1px solid rgba(59, 130, 246, 0.2)", paddingTop: "8px", display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                        <span>{t(locale, "payments.planConfig.summary.total")}</span>
                        <span>${totalPrice.toFixed(2)}/{period}</span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={handleUpdatePlan}
                  disabled={businessUpdating || needsPaymentMethod}
                  style={{
                    flex: 1,
                    padding: "12px",
                    backgroundColor: businessUpdating ? "var(--color-text-secondary)" : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: businessUpdating ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    opacity: needsPaymentMethod ? 0.6 : 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px"
                  }}
                  title={needsPaymentMethod ? t(locale, "payments.planConfig.needPaymentMethod") : ""}
                  onMouseEnter={(e) => {
                    if (!businessUpdating && !needsPaymentMethod) {
                      (e.target as HTMLButtonElement).style.backgroundColor = "#2563eb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = "#3b82f6";
                  }}
                >
                  {businessUpdating ? t(locale, "payments.planConfig.updating") : (() => {
                    const monthlyTotal = pricing.business_base_price + ((businessStorageGb - pricing.storage_included_business_gb) * pricing.storage_price_per_gb) + ((businessDomainCount - 1) * pricing.domain_price_per_month) + ((businessAliasCount - 5) * pricing.alias_price_per_month);
                    const yearlyMultiplier = businessBillingInterval === "annual" ? 12 * 0.83 : 1;
                    const totalPrice = monthlyTotal * yearlyMultiplier;
                    const period = businessBillingInterval === "monthly" ? "mo" : "yr";
                    return (
                      <>
                        <div>{hasPaid ? t(locale, "payments.planConfig.action.updateSubscription") : t(locale, "payments.planConfig.action.createSubscription")}</div>
                        <div style={{ fontSize: "13px", opacity: 0.9 }}>${totalPrice.toFixed(2)} / {period}</div>
                      </>
                    );
                  })()}
                </button>
                <button
                  onClick={() => setShowBusinessPlanModal(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600
                  }}
                >
                  {t(locale, "common.cancel")}
                </button>
              </div>
            </div>
          </div>

          {/* Add animation styles */}
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}</style>
        </>
      )}

      {/* Delete payment method confirmation */}
      {showDeleteMethod && (
        <div style={{
          padding: "15px",
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          borderRadius: "6px",
          marginBottom: "20px"
        }}>
          <p>{t(locale, "payments.deleteMethod.confirm")}</p>
          <button
            onClick={handleDeleteMethod}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-error)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginRight: "8px"
            }}
          >
            {t(locale, "payments.deleteMethod.btn")}
          </button>
          <button
            onClick={() => setShowDeleteMethod(null)}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            {t(locale, "common.cancel")}
          </button>
        </div>
      )}

      {/* Cancel confirmation */}
      {showCancelConfirm && (
        <div style={{
          padding: "15px",
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          borderRadius: "6px",
          marginBottom: "20px"
        }}>
          <p>
            {t(locale, "payments.cancel.accessNote", { date: status?.subscription?.current_period_end ? formatDate(status.subscription.current_period_end) : "end of period" })}
          </p>
          <button
            onClick={handleCancel}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-error)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginRight: "8px"
            }}
          >
            {t(locale, "payments.cancelSubscription")}
          </button>
          <button
            onClick={() => setShowCancelConfirm(false)}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            {t(locale, "payments.keepSubscription")}
          </button>
        </div>
      )}

      {/* Refund confirmation */}
      {showRefundConfirm && (
        <div style={{
          padding: "15px",
          backgroundColor: "rgba(234, 179, 8, 0.1)",
          borderRadius: "6px",
          marginBottom: "20px"
        }}>
          <p>{t(locale, "payments.refund.confirmText")}</p>
          <textarea
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder={t(locale, "payments.refund.reasonPlaceholder")}
            style={{
              width: "100%",
              minHeight: "60px",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "4px",
              border: "1px solid var(--color-border)",
              fontFamily: "inherit",
              fontSize: "13px"
            }}
          />
          <button
            onClick={handleRequestRefund}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-warning)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginRight: "8px"
            }}
          >
            {t(locale, "payments.requestRefund")}
          </button>
          <button
            onClick={() => setShowRefundConfirm(false)}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            {t(locale, "common.cancel")}
          </button>
        </div>
      )}

      {/* Add card modal */}
      {showAddCard && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: "var(--canvas)",
            borderRadius: "8px",
            padding: "20px",
            maxWidth: "500px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)"
          }}>
            <h3 style={{ marginTop: 0, marginBottom: "4px" }}>{t(locale, "payments.addCard.title")}</h3>
            <p style={{ color: "var(--color-text-secondary)", fontSize: "13px", marginBottom: "20px" }}>
              {t(locale, "payments.addCard.description")}
            </p>

            {setupIntentLoading ? (
              <div style={{ padding: "20px", textAlign: "center", color: "var(--color-text-secondary)" }}>
                {t(locale, "payments.addCard.loading")}
              </div>
            ) : setupIntentError ? (
              <div style={{
                padding: "12px",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                borderLeft: "3px solid #ef4444",
                borderRadius: "4px",
                color: "#ef4444",
                fontSize: "13px",
                marginBottom: "20px"
              }}>
                {setupIntentError}
              </div>
            ) : setupIntentClientSecret ? (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret: setupIntentClientSecret }}
              >
                <PaymentElementForm
                  onClose={() => {
                    setShowAddCard(false);
                    setSetupIntentClientSecret(null);
                  }}
                  onSuccess={() => {
                    setShowAddCard(false);
                    setSetupIntentClientSecret(null);
                    fetchAll();
                  }}
                  locale={locale}
                />
              </Elements>
            ) : null}
          </div>
        </div>
      )}

      {/* Storage request modal */}
      {showStorageModal && (
        <div style={{
          padding: "20px",
          backgroundColor: "var(--color-bg-secondary)",
          borderRadius: "6px",
          marginBottom: "20px",
          border: "1px solid var(--color-border)"
        }}>
          <h4 style={{ marginTop: 0 }}>{t(locale, "payments.storage.title")}</h4>
          <label style={{ display: "block", marginBottom: "8px", fontSize: "14px", fontWeight: 600 }}>
            {t(locale, "payments.storage.label")}
          </label>
          <input
            type="number"
            min={201}
            max={10000}
            step={50}
            value={storageRequest}
            onChange={(e) => setStorageRequest(Number(e.target.value))}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "6px",
              borderRadius: "4px",
              border: "1px solid var(--color-border)",
              fontSize: "13px"
            }}
          />
          {storageRequest > 200 && (
            <div style={{ fontSize: "12px", color: "var(--color-text-secondary)", marginBottom: "12px" }}>
              Estimated cost: ${((storageRequest - 200) * pricing.storage_price_per_gb).toFixed(2)}/mo above 200GB
            </div>
          )}
          <button
            onClick={handleRequestStorage}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-primary)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginRight: "8px",
              fontSize: "13px",
              fontWeight: 600
            }}
          >
            {t(locale, "payments.storage.submit")}
          </button>
          <button
            onClick={() => setShowStorageModal(false)}
            style={{
              padding: "8px 16px",
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border)",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px"
            }}
          >
            {t(locale, "common.cancel")}
          </button>
        </div>
      )}
    </div>
  );
}
